import React, { useState, useMemo } from 'react';
import { Card, CardHeader, CardContent } from '../components/ui/Card';
import { Button } from '../components/ui/Button';
import SafeIcon from '../common/SafeIcon';
import { motion, AnimatePresence } from 'framer-motion';
import { standardPrinciples, certificationRequirements, calculateLevelStatus } from '../data/nordicStandardLogic';

export const NordicStandard = () => {
  const [completedReqs, setCompletedReqs] = useState(['r1a', 'r1b', 'r1c']);
  const [showCertificate, setShowCertificate] = useState(false);

  const { status, currentLevel } = useMemo(() => calculateLevelStatus(completedReqs), [completedReqs]);

  const toggleReq = (id) => {
    setCompletedReqs(prev => prev.includes(id) ? prev.filter(i => i !== id) : [...prev, id]);
  };

  return (
    <div className="space-y-8 pb-20">
      <div className="flex justify-between items-end">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Nordic Standard</h1>
          <p className="text-slate-500 mt-1">Sertifisering for psykososialt arbeidsmiljø v2.0</p>
        </div>
        <Button 
          variant={currentLevel > 0 ? "primary" : "secondary"} 
          disabled={currentLevel === 0}
          onClick={() => setShowCertificate(true)}
          icon={<SafeIcon name="Award" />}
        >
          Se Sertifikat
        </Button>
      </div>

      {/* Principles Grid */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
        {standardPrinciples.map((p) => (
          <Card key={p.id} className="border-none shadow-sm bg-white hover:shadow-md transition-shadow">
            <CardContent className="p-4 text-center">
              <div className="w-10 h-10 rounded-xl bg-indigo-50 text-indigo-600 flex items-center justify-center mx-auto mb-3">
                <SafeIcon name={p.icon} />
              </div>
              <h4 className="text-xs font-bold text-slate-900 mb-1">{p.name}</h4>
              <p className="text-[10px] text-slate-400 leading-tight line-clamp-2">{p.desc}</p>
            </CardContent>
          </Card>
        ))}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        {/* Requirement Columns */}
        <div className="lg:col-span-2 space-y-6">
          {status.map((lvl) => (
            <Card key={lvl.id} className={`${lvl.isComplete ? 'border-emerald-200 bg-emerald-50/10' : 'border-slate-200'}`}>
              <CardHeader 
                title={lvl.title} 
                subtitle={`Nivå ${lvl.level}`} 
                icon={<div className={`p-2 rounded-lg ${lvl.isComplete ? 'bg-emerald-100 text-emerald-600' : 'bg-slate-100 text-slate-500'}`}><SafeIcon name="CheckCircle" /></div>}
                action={<span className="text-xs font-black text-slate-400">{Math.round(lvl.progress)}%</span>}
              />
              <CardContent className="p-0">
                <div className="divide-y divide-slate-100">
                  {lvl.requirements.map((req) => (
                    <div 
                      key={req.id} 
                      onClick={() => toggleReq(req.id)}
                      className="flex items-center gap-4 p-4 hover:bg-slate-50 cursor-pointer transition-colors"
                    >
                      <div className={`w-5 h-5 rounded border-2 flex items-center justify-center transition-all ${completedReqs.includes(req.id) ? 'bg-indigo-600 border-indigo-600 text-white' : 'border-slate-200 bg-white'}`}>
                        {completedReqs.includes(req.id) && <SafeIcon name="Check" size={12} />}
                      </div>
                      <span className={`text-sm font-medium ${completedReqs.includes(req.id) ? 'text-slate-900' : 'text-slate-500'}`}>
                        {req.text}
                      </span>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          ))}
        </div>

        {/* Sidebar Info */}
        <div className="space-y-6">
          <Card className="bg-slate-900 border-none text-white overflow-hidden relative">
            <div className="absolute top-0 right-0 w-32 h-32 bg-indigo-500/20 rounded-full blur-2xl -mr-10 -mt-10"></div>
            <CardContent className="p-8 space-y-6 relative z-10">
              <div className="w-16 h-16 rounded-2xl bg-white/10 flex items-center justify-center text-white backdrop-blur-md">
                <SafeIcon name="ShieldCheck" className="text-3xl text-indigo-400" />
              </div>
              <h3 className="text-xl font-bold">Status: Nivå {currentLevel}</h3>
              <p className="text-sm text-slate-400 leading-relaxed">
                Din virksomhet oppfyller kravene for {currentLevel === 0 ? 'ingen sertifisering ennå' : `Nivå ${currentLevel}: ${status[currentLevel-1].title}`}.
              </p>
              {currentLevel < 3 && (
                <div className="p-4 bg-white/5 rounded-xl border border-white/10">
                  <p className="text-[10px] font-bold text-indigo-300 uppercase mb-2">Neste Milepæl</p>
                  <p className="text-xs text-white font-medium">Fullfør "{status[currentLevel].title}" for å øke din attraktivitet som arbeidsgiver.</p>
                </div>
              )}
            </CardContent>
          </Card>

          <Card>
            <CardHeader title="Dokumentasjon" icon={<SafeIcon name="FileText" />} />
            <CardContent className="space-y-4">
              <p className="text-xs text-slate-500 leading-relaxed">
                For å oppnå nivå 2 og 3 kreves det revisjon av ekstern konsulent. Last opp dine bevis her.
              </p>
              <Button variant="secondary" className="w-full text-xs" icon={<SafeIcon name="Upload" />}>
                Last opp bevis (.pdf)
              </Button>
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Certificate Modal Overlay */}
      <AnimatePresence>
        {showCertificate && (
          <motion.div 
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 flex items-center justify-center p-6 bg-slate-900/80 backdrop-blur-sm"
            onClick={() => setShowCertificate(false)}
          >
            <motion.div 
              initial={{ scale: 0.9, y: 20 }} animate={{ scale: 1, y: 0 }} exit={{ scale: 0.9, y: 20 }}
              className="max-w-2xl w-full bg-white rounded-[40px] shadow-2xl overflow-hidden text-center p-16 relative"
              onClick={e => e.stopPropagation()}
            >
              <div className="absolute top-0 left-0 w-full h-2 bg-gradient-to-r from-indigo-500 via-emerald-500 to-indigo-500"></div>
              <div className="flex justify-center mb-8">
                <div className="w-24 h-24 rounded-full bg-indigo-50 flex items-center justify-center border-4 border-indigo-100">
                  <SafeIcon name="Hexagon" className="text-5xl text-indigo-600" />
                </div>
              </div>
              <p className="text-xs font-black text-indigo-600 uppercase tracking-[0.3em] mb-4">Nordic Workplace Certificate</p>
              <h2 className="text-4xl font-black text-slate-900 mb-6 tracking-tight">TechCorp AS</h2>
              <div className="w-24 h-1 bg-slate-200 mx-auto mb-8"></div>
              <p className="text-lg text-slate-600 leading-relaxed max-w-md mx-auto mb-10">
                Sertifisert i henhold til <b>Nordic Standard for Psychosocial Work Environment</b> på nivå {currentLevel}.
              </p>
              <div className="flex justify-center gap-12 mb-12">
                <div className="text-center">
                  <p className="text-[10px] font-bold text-slate-400 uppercase mb-1">Utstedt</p>
                  <p className="text-sm font-bold text-slate-900">14. Mai 2024</p>
                </div>
                <div className="text-center">
                  <p className="text-[10px] font-bold text-slate-400 uppercase mb-1">Gyldig til</p>
                  <p className="text-sm font-bold text-slate-900">14. Mai 2025</p>
                </div>
              </div>
              <div className="flex flex-col gap-3">
                <Button variant="primary" className="w-full" icon={<SafeIcon name="Download" />}>Last ned original PDF</Button>
                <button onClick={() => setShowCertificate(false)} className="text-xs text-slate-400 hover:text-slate-600 transition-colors">Lukk forhåndsvisning</button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};